import React, { memo } from 'react';
import { ButtonCTA } from '../components/ButtonCTA';
import { FAQAccordion } from '../components/FAQAccordion';
import { CountdownTimer } from '../components/CountdownTimer';
import { RecentPurchaseToast } from '../components/RecentPurchaseToast';
import { CheckCircle2, Star, Clock, ShieldCheck, Sparkles, Moon, BookOpen, Heart, Activity, ArrowDown, Gift, Zap, Brain, Coffee, Leaf, Flame, Lock, Download, Award, Users, TrendingUp } from 'lucide-react';
export function LandingPage() {
  return <div className="min-h-screen bg-sand-50 font-sans text-earth-900 selection:bg-terracotta-100 selection:text-terracotta-700 overflow-x-hidden" data-id="element-36">
      {/* 1. Sticky Announcement Bar with Countdown - reverted */}
      <div className="sticky top-0 z-50 bg-gradient-to-r from-terracotta-700 via-terracotta-600 to-terracotta-700 text-white py-2.5 px-4 text-center shadow-lg border-b-2 border-gold-500" data-id="element-37">
        <div className="flex items-center justify-center gap-2 sm:gap-4 flex-wrap text-sm sm:text-base" data-id="element-38">
          <Flame className="w-4 h-4 sm:w-5 sm:h-5 text-gold-500 animate-pulse" data-id="element-39" />
          <span className="font-bold tracking-wide" data-id="element-40">82% OFF — Termina en:</span>
          <CountdownTimer variant="bar" data-id="element-41" />
          <Flame className="w-4 h-4 sm:w-5 sm:h-5 text-gold-500 animate-pulse" data-id="element-42" />
        </div>
      </div>

      {/* Scarcity Bar */}
      <div className="bg-earth-900 text-sand-50 py-2 px-4 text-center text-xs sm:text-sm" data-id="element-43">
        <span className="inline-flex items-center gap-2 flex-wrap justify-center" data-id="element-44">
          <Users className="w-4 h-4 text-gold-500" data-id="element-45" />
          <span data-id="element-46">
            <span className="font-bold text-gold-500" data-id="element-47">347 personas</span> están
            viendo esta página ahora
          </span>
          <span className="hidden sm:inline text-earth-600" data-id="element-48">•</span>
          <span data-id="element-49">
            <span className="font-bold text-gold-500" data-id="element-50">+2.000 personas</span> ya
            lo compraron
          </span>
        </span>
      </div>

      {/* 2. Hero Section — single column, no bundle visual */}
      <section className="relative pt-10 pb-16 lg:pt-16 lg:pb-24 px-4 overflow-hidden" data-id="element-51">
        {/* Background blurs */}
        <div className="absolute top-0 left-0 w-full h-full overflow-hidden -z-10 pointer-events-none" data-id="element-52">
          <div className="absolute top-[-10%] right-[-5%] w-[500px] h-[500px] rounded-full bg-blush-100/70 blur-3xl" data-id="element-53"></div>
          <div className="absolute bottom-[-10%] left-[-10%] w-[600px] h-[600px] rounded-full bg-sage-100/60 blur-3xl" data-id="element-54"></div>
          <div className="absolute top-1/2 left-1/2 w-[400px] h-[400px] rounded-full bg-gold-500/10 blur-3xl" data-id="element-55"></div>
        </div>

        <div className="max-w-3xl mx-auto relative z-10" data-id="element-56">
          <div className="space-y-6 relative z-10" data-id="element-57">
            {/* Refined gold ribbon badge */}
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-gradient-to-r from-gold-500/20 via-gold-500/30 to-gold-500/20 border border-gold-500/40 text-earth-900 text-xs sm:text-sm font-bold tracking-[0.15em] uppercase" data-id="element-58">
              <Sparkles className="w-3.5 h-3.5 text-gold-500" data-id="element-59" />
              Bienestar Natural · Argentina
              <Sparkles className="w-3.5 h-3.5 text-gold-500" data-id="element-60" />
            </div>

            {/* Captivating italic eyebrow */}
            <p className="font-serif italic text-xl sm:text-2xl text-terracotta-700 leading-snug" data-id="element-61">
              Imaginá despertarte mañana sin ese dolor de cuello, sin la
              mandíbula apretada, sin esa pesadez en el cuerpo…
            </p>

            <h1 className="text-4xl sm:text-5xl lg:text-[3.25rem] font-serif font-bold leading-[1.1] text-earth-900 tracking-tight" data-id="element-62">
              Aprendé qué
              <span className="relative inline-block mx-1.5" data-id="element-63">
                <span className="relative z-10" data-id="element-64">puntos tocar</span>
                <span className="absolute bottom-1 left-0 right-0 h-3 bg-gold-500/40 -z-0 rounded" data-id="element-65"></span>
              </span>
              para aliviar dolor de cuello, cabeza, tensión y ansiedad — desde
              casa.
            </h1>

            <p className="text-base sm:text-lg text-earth-700 leading-relaxed" data-id="element-66">
              Un método práctico de{' '}
              <strong data-id="element-67">Auriculoterapia + Reflexología</strong> que te muestra los
              puntos exactos del oído, las manos y los pies para liberar tensión
              y recuperar tu bienestar físico.{' '}
              <span className="font-semibold text-earth-900" data-id="element-68">
                Sin agujas. Sin turnos. Sin médicos.
              </span>
            </p>

            {/* Practical method icons */}
            <div className="flex flex-wrap items-center gap-2 sm:gap-3 pt-1" data-id="element-69">
              {[{
              icon: Brain,
              label: 'Cabeza'
            }, {
              icon: Activity,
              label: 'Cuello'
            }, {
              icon: Heart,
              label: 'Mandíbula'
            }, {
              icon: Moon,
              label: 'Sueño'
            }].map((t, i) => <div key={i} className="flex items-center gap-1.5 bg-sand-100 border border-sand-200 rounded-full px-3 py-1.5 text-xs sm:text-sm text-earth-800 font-medium" data-id="element-70">
                  <t.icon className="w-3.5 h-3.5 text-terracotta-600" data-id="element-71" />
                  <span data-id="element-72">{t.label}</span>
                </div>)}
            </div>

            <ul className="grid grid-cols-2 gap-x-4 gap-y-2 text-earth-800 text-sm sm:text-base" data-id="element-73">
              {['Migrañas', 'Dolor cervical', 'Contracturas', 'Insomnio', 'Ansiedad física', 'Estrés acumulado', 'Cansancio crónico', 'Tensión corporal'].map((item, i) => <li key={i} className="flex items-center gap-2" data-id="element-74">
                  <CheckCircle2 className="w-4 h-4 text-sage-500 flex-shrink-0" data-id="element-75" />
                  <span data-id="element-76">{item}</span>
                </li>)}
            </ul>

            <div className="pt-2 space-y-4" data-id="element-77">
              <ButtonCTA text="QUIERO EMPEZAR HOY" subtext="Acceso inmediato · Garantía 7 días" showGuarantee data-id="element-78" />

              {/* Trust row */}
              <div className="flex flex-wrap items-center gap-x-5 gap-y-2 text-sm text-earth-700" data-id="element-79">
                <div className="flex items-center gap-1.5" data-id="element-80">
                  <div className="flex text-gold-500" data-id="element-81">
                    {[1, 2, 3, 4, 5].map(i => <Star key={i} className="w-4 h-4 fill-current" data-id="element-82" />)}
                  </div>
                  <span className="font-semibold" data-id="element-83">4.9/5</span>
                </div>
                <span className="text-earth-400" data-id="element-84">·</span>
                <div className="flex items-center gap-1.5" data-id="element-85">
                  <Users className="w-4 h-4 text-sage-600" data-id="element-86" />
                  <span data-id="element-87">
                    <strong data-id="element-88">+2.000</strong> personas ya lo usan
                  </span>
                </div>
              </div>

              {/* Hero countdown — slimmer, elegant */}
              <div className="bg-earth-900 rounded-2xl p-4 text-center max-w-md border border-gold-500/30" data-id="element-89">
                <p className="text-gold-500 text-xs font-bold uppercase tracking-[0.15em] mb-2 flex items-center justify-center gap-2" data-id="element-90">
                  <Clock className="w-3.5 h-3.5 animate-pulse" data-id="element-91" /> La oferta
                  termina en
                </p>
                <CountdownTimer data-id="element-92" />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* 3. Problem Section — calm version, no cost-of-inaction block */}
      <section className="py-20 bg-earth-900 text-sand-50 px-4 relative overflow-hidden" data-id="element-93">
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-full max-w-4xl h-full bg-[radial-gradient(ellipse_at_top,_var(--tw-gradient-stops))] from-terracotta-900/40 via-transparent to-transparent pointer-events-none" data-id="element-94"></div>

        <div className="max-w-4xl mx-auto text-center space-y-12 relative z-10" data-id="element-95">
          <h2 className="text-3xl sm:text-4xl md:text-5xl font-serif font-bold text-terracotta-100" data-id="element-96">
            ¿Te duele el cuerpo todo el tiempo?
          </h2>

          <div className="flex flex-wrap justify-center gap-3 sm:gap-4" data-id="element-97">
            {['Dolor de cabeza', 'Cuello duro', 'Espalda tensionada', 'Mandíbula apretada', 'Migrañas', 'Insomnio', 'Agotamiento constante'].map((symptom, i) => <span key={i} className="px-4 py-2 rounded-full bg-earth-800/50 border border-earth-700 text-sand-100 font-medium" data-id="element-98">
                {symptom}
              </span>)}
          </div>

          <div className="space-y-6 text-xl sm:text-2xl font-serif text-sand-200 leading-relaxed max-w-3xl mx-auto" data-id="element-99">
            <p data-id="element-100">Dolor físico que aparece una y otra vez.</p>
            <p data-id="element-101">Y aunque descanses… el cuerpo nunca termina de sentirse bien.</p>
            <p className="text-3xl sm:text-4xl font-bold text-white pt-4" data-id="element-102">
              ESO NO ES NORMAL.
            </p>
          </div>

          <p className="text-lg text-earth-300 max-w-2xl mx-auto" data-id="element-103">
            Tu cuerpo está sobrecargado. Y cuanto más tiempo pasa, más acumulás
            tensión, dolor y malestar físico.
          </p>
        </div>
      </section>

      {/* NEW: ¿Por qué esto SÍ funciona? — Comparison section */}
      <section className="py-20 px-4 bg-sand-50 relative" data-id="element-104">
        <div className="max-w-5xl mx-auto" data-id="element-105">
          <div className="text-center mb-12" data-id="element-106">
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-terracotta-50 border border-terracotta-200 text-terracotta-700 text-xs sm:text-sm font-bold tracking-wide uppercase mb-4" data-id="element-107">
              <Sparkles className="w-3.5 h-3.5" data-id="element-108" />
              La diferencia
            </div>
            <h2 className="text-3xl sm:text-4xl md:text-5xl font-serif font-bold text-earth-900 mb-4 leading-tight" data-id="element-109">
              ¿Por qué este método{' '}
              <span className="text-terracotta-600 italic" data-id="element-110">sí funciona</span> y
              otras cosas no?
            </h2>
            <p className="text-lg text-earth-700 max-w-2xl mx-auto" data-id="element-111">
              Si ya probaste otras opciones y el dolor sigue volviendo, esto es
              lo que las diferencia:
            </p>
          </div>

          <div className="bg-white rounded-3xl shadow-lg border border-sand-200 overflow-hidden" data-id="element-112">
            {/* Header row */}
            <div className="grid grid-cols-5 bg-earth-900 text-white text-xs sm:text-sm font-bold uppercase tracking-wider" data-id="element-113">
              <div className="p-4 sm:p-5 col-span-2" data-id="element-114">Lo que probaste</div>
              <div className="p-4 sm:p-5 text-center border-l border-earth-700" data-id="element-115">
                Costo
              </div>
              <div className="p-4 sm:p-5 text-center border-l border-earth-700" data-id="element-116">
                Resultados
              </div>
              <div className="p-4 sm:p-5 text-center border-l border-earth-700" data-id="element-117">
                En casa
              </div>
            </div>

            {[{
            name: 'Analgésicos',
            cost: 'Recurrente',
            result: 'Apaga el síntoma',
            home: false,
            X: true
          }, {
            name: 'Kinesiólogo',
            cost: '$$$ por sesión',
            result: 'Mejora temporal',
            home: false,
            X: true
          }, {
            name: 'Videos de YouTube',
            cost: 'Gratis',
            result: 'Información dispersa',
            home: true,
            X: true
          }, {
            name: 'Tratamientos alternativos',
            cost: '$$$$',
            result: 'Variable',
            home: false,
            X: true
          }].map((row, i) => <div key={i} className="grid grid-cols-5 border-t border-sand-200 text-earth-800" data-id="element-118">
                <div className="p-4 sm:p-5 col-span-2 font-semibold" data-id="element-119">
                  {row.name}
                </div>
                <div className="p-4 sm:p-5 text-center text-sm border-l border-sand-200" data-id="element-120">
                  {row.cost}
                </div>
                <div className="p-4 sm:p-5 text-center text-sm border-l border-sand-200" data-id="element-121">
                  {row.result}
                </div>
                <div className="p-4 sm:p-5 text-center border-l border-sand-200" data-id="element-122">
                  {row.home ? <CheckCircle2 className="w-5 h-5 text-sage-500 mx-auto" data-id="element-123" /> : <span className="text-red-400 font-bold text-xl" data-id="element-124">✗</span>}
                </div>
              </div>)}

            {/* This product row — highlighted */}
            <div className="grid grid-cols-5 border-t-2 border-gold-500 bg-gradient-to-r from-gold-500/10 via-sand-50 to-gold-500/10 text-earth-900" data-id="element-125">
              <div className="p-4 sm:p-5 col-span-2 font-bold flex items-center gap-2" data-id="element-126">
                <Sparkles className="w-5 h-5 text-gold-500" data-id="element-127" />
                Este bundle
              </div>
              <div className="p-4 sm:p-5 text-center text-sm border-l border-sand-200 font-bold" data-id="element-128">
                $19.990 una vez
              </div>
              <div className="p-4 sm:p-5 text-center text-sm border-l border-sand-200 font-bold" data-id="element-129">
                Alivio duradero
              </div>
              <div className="p-4 sm:p-5 text-center border-l border-sand-200" data-id="element-130">
                <CheckCircle2 className="w-6 h-6 text-sage-600 mx-auto" data-id="element-131" />
              </div>
            </div>
          </div>

          <div className="mt-10 grid sm:grid-cols-3 gap-4" data-id="element-132">
            {[{
            icon: CheckCircle2,
            label: 'Sin agujas',
            sub: 'Solo presión con tus manos'
          }, {
            icon: CheckCircle2,
            label: 'Pago único',
            sub: 'Sin suscripciones ni cuotas'
          }, {
            icon: CheckCircle2,
            label: 'Para siempre',
            sub: 'Lo usás cuando lo necesites'
          }].map((item, i) => <div key={i} className="flex items-start gap-3 p-4 bg-white rounded-2xl border border-sand-200" data-id="element-133">
                <item.icon className="w-5 h-5 text-sage-600 flex-shrink-0 mt-0.5" data-id="element-134" />
                <div data-id="element-135">
                  <p className="font-bold text-earth-900" data-id="element-136">{item.label}</p>
                  <p className="text-sm text-earth-600" data-id="element-137">{item.sub}</p>
                </div>
              </div>)}
          </div>

          <div className="mt-12 flex justify-center" data-id="element-138">
            <ButtonCTA text="QUIERO PROBAR EL MÉTODO" subtext="Garantía 7 días • Acceso inmediato" showGuarantee data-id="element-139" />
          </div>
        </div>
      </section>

      {/* 4. Transformation Section — RESTORED */}
      <section className="py-20 px-4 bg-white" data-id="element-140">
        <div className="max-w-5xl mx-auto" data-id="element-141">
          <div className="text-center mb-16" data-id="element-142">
            <h2 className="text-3xl sm:text-4xl font-serif font-bold text-earth-900 mb-4" data-id="element-143">
              Este sistema es para vos si…
            </h2>
            <div className="w-24 h-1 bg-sage-500 mx-auto rounded-full" data-id="element-144"></div>
          </div>

          <div className="grid md:grid-cols-2 gap-6 sm:gap-8" data-id="element-145">
            {[{
            icon: Activity,
            text: 'Te duele el cuerpo y no sabés por dónde empezar'
          }, {
            icon: Zap,
            text: "Vivís tomando analgésicos o esperando 'a que se pase'"
          }, {
            icon: Heart,
            text: 'Tenés tensión en cuello, espalda, mandíbula o cabeza'
          }, {
            icon: Moon,
            text: 'Dormís mal y te levantás sin energía'
          }, {
            icon: Brain,
            text: 'Sentís que tu cuerpo está siempre en alerta'
          }, {
            icon: Leaf,
            text: 'Querés herramientas simples para usar en casa'
          }].map((item, i) => <div key={i} className="flex items-start gap-4 p-6 rounded-2xl bg-sand-50 border border-sand-100 hover:shadow-md transition-shadow" data-id="element-146">
                <div className="w-12 h-12 rounded-full bg-terracotta-100 flex items-center justify-center flex-shrink-0" data-id="element-147">
                  <item.icon className="w-6 h-6 text-terracotta-600" data-id="element-148" />
                </div>
                <p className="text-lg text-earth-800 font-medium pt-2" data-id="element-149">
                  {item.text}
                </p>
              </div>)}

            <div className="md:col-span-2 bg-sage-50 p-6 sm:p-8 rounded-2xl border border-sage-100 flex items-center gap-4 justify-center text-center" data-id="element-150">
              <Sparkles className="w-8 h-8 text-sage-600 flex-shrink-0 hidden sm:block" data-id="element-151" />
              <p className="text-xl font-serif font-medium text-sage-900" data-id="element-152">
                Necesitás una guía clara: qué punto tocar, dónde y por cuánto
                tiempo.
              </p>
            </div>
          </div>

          <div className="mt-16 flex justify-center" data-id="element-153">
            <ButtonCTA text="QUIERO EMPEZAR A SENTIRME MEJOR" data-id="element-154" />
          </div>
        </div>
      </section>

      {/* 5. Product Breakdown — RESTORED with screenshot previews */}
      <section className="py-20 px-4 bg-sand-100 relative" data-id="element-155">
        <div className="max-w-6xl mx-auto space-y-20" data-id="element-156">
          <div className="text-center" data-id="element-157">
            <h2 className="text-3xl sm:text-4xl font-serif font-bold text-earth-900 mb-4" data-id="element-158">
              ¿Qué incluye el Bundle?
            </h2>
            <p className="text-lg text-earth-600" data-id="element-159">
              Todo lo que necesitás para empezar hoy mismo.
            </p>
          </div>

          {/* Ebook 1 — Reflexología */}
          <div className="space-y-8" data-id="element-160">
            <div className="grid md:grid-cols-2 gap-12 items-center bg-white p-8 sm:p-12 rounded-[2.5rem] shadow-sm border border-sand-200" data-id="element-161">
              <div className="order-2 md:order-1 space-y-6" data-id="element-162">
                <div className="inline-block px-4 py-1.5 bg-sage-100 text-sage-800 rounded-full text-sm font-bold tracking-wide" data-id="element-163">
                  EBOOK 1
                </div>
                <h3 className="text-3xl font-serif font-bold text-earth-900" data-id="element-164">
                  Alivio en tus Manos
                </h3>
                <p className="text-xl text-earth-600 font-serif italic" data-id="element-165">
                  Reflexología para aliviar dolor físico y recuperar el
                  equilibrio del cuerpo
                </p>
                <p className="text-earth-800" data-id="element-166">
                  Aprendé protocolos específicos para:
                </p>
                <ul className="grid sm:grid-cols-2 gap-3" data-id="element-167">
                  {['Migrañas', 'Dolor cervical', 'Dolor lumbar', 'Contracturas', 'Estrés físico', 'Insomnio', 'Dolor menstrual', 'Ansiedad'].map((item, i) => <li key={i} className="flex items-center gap-2 text-earth-700" data-id="element-168">
                      <CheckCircle2 className="w-5 h-5 text-sage-500" data-id="element-169" /> {item}
                    </li>)}
                </ul>
              </div>
              <div className="order-1 md:order-2 flex items-center justify-center relative" data-id="element-170">
                <div className="absolute inset-0 bg-gradient-to-tr from-sage-100 to-blush-100 rounded-3xl blur-2xl opacity-60 -z-10" data-id="element-171"></div>
                <img src="https://cdn.magicpatterns.com/uploads/pZnPaA6mBGpi2qvCE38CQs/ALIVIO_EN_TUS_MANOS.png" alt="Ebook Alivio en tus Manos — Reflexología" className="w-full max-w-sm h-auto rounded-2xl shadow-2xl rotate-[-3deg] hover:rotate-0 transition-transform duration-500" data-id="element-172" />
              </div>
            </div>

            {/* Previews — Ebook 1 */}
            <div className="bg-white p-6 sm:p-8 rounded-[2rem] border border-sand-200" data-id="element-173">
              <p className="text-center text-sm font-bold uppercase tracking-wider text-sage-700 mb-2" data-id="element-174">
                ✦ Una mirada adentro
              </p>
              <h4 className="text-center text-2xl font-serif font-bold text-earth-900 mb-6" data-id="element-175">
                Esto es lo que vas a encontrar
              </h4>
              <div className="grid sm:grid-cols-2 gap-4 sm:gap-6" data-id="element-176">
                {['https://cdn.magicpatterns.com/uploads/8wrEVoNdQpZNahdYmgMLzS/Captura_de_pantalla_2026-05-11_194708.png', 'https://cdn.magicpatterns.com/uploads/p9AaPtr5kbUN6fFbQkiFxr/Captura_de_pantalla_2026-05-11_194737.png'].map((src, i) => <div key={i} className="aspect-[3/4] overflow-hidden rounded-2xl border border-sand-200 shadow-md hover:shadow-xl transition-shadow bg-earth-900" data-id="element-177">
                    <img src={src} alt={`Vista previa Ebook 1 — página ${i + 1}`} className="w-full h-full object-cover object-top" data-id="element-178" />
                  </div>)}
              </div>
            </div>
          </div>

          {/* Ebook 2 — Auriculoterapia */}
          <div className="space-y-8" data-id="element-179">
            <div className="grid md:grid-cols-2 gap-12 items-center bg-white p-8 sm:p-12 rounded-[2.5rem] shadow-sm border border-sand-200" data-id="element-180">
              <div className="flex items-center justify-center relative" data-id="element-181">
                <div className="absolute inset-0 bg-gradient-to-tr from-terracotta-100 to-blush-100 rounded-3xl blur-2xl opacity-60 -z-10" data-id="element-182"></div>
                <img src="https://cdn.magicpatterns.com/uploads/oqinBveycV1kdDYFdFy3sd/iculoterapia.png" alt="Ebook Puntos que Sanan — Auriculoterapia" className="w-full max-w-sm h-auto rounded-2xl shadow-2xl rotate-[3deg] hover:rotate-0 transition-transform duration-500" data-id="element-183" />
              </div>
              <div className="space-y-6" data-id="element-184">
                <div className="inline-block px-4 py-1.5 bg-terracotta-100 text-terracotta-800 rounded-full text-sm font-bold tracking-wide" data-id="element-185">
                  EBOOK 2
                </div>
                <h3 className="text-3xl font-serif font-bold text-earth-900" data-id="element-186">
                  Puntos que Sanan
                </h3>
                <p className="text-xl text-earth-600 font-serif italic" data-id="element-187">
                  Auriculoterapia práctica para desactivar síntomas físicos
                  naturalmente
                </p>
                <p className="text-earth-800" data-id="element-188">
                  Descubrí los puntos exactos del oído para trabajar:
                </p>
                <ul className="grid sm:grid-cols-2 gap-3" data-id="element-189">
                  {['Dolor físico', 'Tensión muscular', 'Sistema nervioso', 'Estrés acumulado', 'Ansiedad física', 'Sueño y descanso', 'Inflamación'].map((item, i) => <li key={i} className="flex items-center gap-2 text-earth-700" data-id="element-190">
                      <CheckCircle2 className="w-5 h-5 text-terracotta-500" data-id="element-191" />{' '}
                      {item}
                    </li>)}
                </ul>
              </div>
            </div>

            {/* Previews — Ebook 2 */}
            <div className="bg-white p-6 sm:p-8 rounded-[2rem] border border-sand-200" data-id="element-192">
              <p className="text-center text-sm font-bold uppercase tracking-wider text-terracotta-700 mb-2" data-id="element-193">
                ✦ Una mirada adentro
              </p>
              <h4 className="text-center text-2xl font-serif font-bold text-earth-900 mb-6" data-id="element-194">
                Esto es lo que vas a encontrar
              </h4>
              <div className="grid sm:grid-cols-2 gap-4 sm:gap-6" data-id="element-195">
                {['https://cdn.magicpatterns.com/uploads/hTeJ3cbCt4ESMj1MNUC4Yc/Captura_de_pantalla_2026-05-11_194903.png', 'https://cdn.magicpatterns.com/uploads/hKTUYJ36GWcEMD6CxjVHvA/Captura_de_pantalla_2026-05-11_194942.png'].map((src, i) => <div key={i} className="aspect-[3/4] overflow-hidden rounded-2xl border border-sand-200 shadow-md hover:shadow-xl transition-shadow bg-earth-900" data-id="element-196">
                    <img src={src} alt={`Vista previa Ebook 2 — página ${i + 1}`} className="w-full h-full object-cover object-top" data-id="element-197" />
                  </div>)}
              </div>
            </div>
          </div>

          <div className="flex justify-center pt-4" data-id="element-198">
            <ButtonCTA text="QUIERO ACCEDER AHORA" showGuarantee data-id="element-199" />
          </div>
        </div>
      </section>

      {/* 6. Bonus Stack — RESTORED */}
      <section className="py-20 px-4 bg-earth-900 text-sand-50 relative overflow-hidden" data-id="element-200">
        <div className="absolute top-0 right-0 w-[800px] h-[800px] bg-terracotta-900/30 rounded-full blur-[100px] pointer-events-none" data-id="element-201"></div>
        <div className="max-w-5xl mx-auto relative z-10" data-id="element-202">
          <div className="text-center mb-16" data-id="element-203">
            <h2 className="text-3xl sm:text-5xl font-serif font-bold text-white mb-6" data-id="element-204">
              Además, si te sumás hoy te llevás{' '}
              <span className="text-gold-500" data-id="element-205">6 BONUS GRATIS</span>
            </h2>
            <p className="text-xl text-earth-300" data-id="element-206">
              Herramientas prácticas para potenciar tus resultados.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6" data-id="element-207">
            {[{
            num: 1,
            title: 'Ritual de Noche',
            desc: 'La rutina de 15 minutos para apagar la tensión física antes de dormir.',
            bullets: ['Rutina nocturna completa', 'Puntos para sueño profundo', 'Técnicas para bajar revoluciones']
          }, {
            num: 2,
            title: '21 Días para Sanar',
            desc: 'El plan progresivo para empezar a sentir cambios reales en tu cuerpo.',
            bullets: ['Calendario de 21 días', 'Prácticas de 5 a 15 min', 'Rutina para crear el hábito']
          }, {
            num: 3,
            title: 'Fichas de Sanación Rápida',
            desc: 'Qué punto tocar según el dolor o síntoma que tengas. Para alivio YA.',
            bullets: ['Migraña y dolor de cabeza', 'Ansiedad aguda e insomnio', 'Dolor lumbar y contracturas']
          }, {
            num: 4,
            title: 'Mapa Completo de Sanación',
            desc: 'Todos los puntos importantes de manos, pies y oído en un solo lugar.',
            bullets: ['Mapa de auriculoterapia', 'Mapa de manos y pies', 'Guía rápida visual']
          }, {
            num: 5,
            title: 'El Poder del Aroma',
            desc: 'Cómo combinar aceites esenciales con auriculoterapia y reflexología.',
            bullets: ['Aceites para dolor y ansiedad', 'Cómo diluirlos correctamente', 'Combinaciones por síntoma']
          }, {
            num: 6,
            title: 'Mi Diario de Sanación',
            desc: 'Registrá tu progreso y descubrí qué técnica funciona mejor para tu cuerpo.',
            bullets: ['Registro diario de síntomas', 'Escala de dolor', 'Tracker de constancia']
          }].map((bonus, i) => <div key={i} className="bg-earth-800/50 border border-earth-700 p-6 rounded-2xl hover:bg-earth-800 transition-colors" data-id="element-208">
                <div className="flex items-center gap-3 mb-4" data-id="element-209">
                  <div className="w-10 h-10 rounded-full bg-gold-500/20 text-gold-500 flex items-center justify-center font-bold" data-id="element-210">
                    <Gift className="w-5 h-5" data-id="element-211" />
                  </div>
                  <h4 className="text-xl font-serif font-bold text-white" data-id="element-212">
                    Bonus {bonus.num}
                  </h4>
                </div>
                <h5 className="text-lg font-bold text-sand-100 mb-2" data-id="element-213">
                  {bonus.title}
                </h5>
                <p className="text-earth-300 text-sm mb-4 h-12" data-id="element-214">{bonus.desc}</p>
                <ul className="space-y-2 mb-6" data-id="element-215">
                  {bonus.bullets.map((b, j) => <li key={j} className="text-sm text-earth-400 flex items-start gap-2" data-id="element-216">
                      <CheckCircle2 className="w-4 h-4 text-sage-500 flex-shrink-0 mt-0.5" data-id="element-217" />
                      {b}
                    </li>)}
                </ul>
                <div className="pt-4 border-t border-earth-700 flex justify-between items-center" data-id="element-218">
                  <span className="text-earth-400 text-sm line-through" data-id="element-219">
                    Valor: $11.990
                  </span>
                  <span className="text-gold-500 font-bold text-sm bg-gold-500/10 px-3 py-1 rounded-full" data-id="element-220">
                    HOY GRATIS
                  </span>
                </div>
              </div>)}
          </div>
        </div>
      </section>

      {/* 7. Testimonials — RESTORED */}
      <section className="py-20 px-4 bg-sand-50" data-id="element-221">
        <div className="max-w-6xl mx-auto" data-id="element-222">
          <div className="text-center mb-16" data-id="element-223">
            <h2 className="text-3xl sm:text-4xl font-serif font-bold text-earth-900 mb-4" data-id="element-224">
              Lo que dicen quienes ya lo probaron
            </h2>
            <div className="flex justify-center gap-1 text-gold-500" data-id="element-225">
              {[1, 2, 3, 4, 5].map(i => <Star key={i} className="w-6 h-6 fill-current" data-id="element-226" />)}
            </div>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6" data-id="element-227">
            {[{
            name: 'Camila R.',
            text: 'Me bajó muchísimo la tensión del cuello. Lo empecé usando por curiosidad y ahora lo tengo guardado en el celular.'
          }, {
            name: 'Martina L.',
            text: 'Yo tenía dolor de cabeza casi todos los días. Las fichas rápidas me ayudaron a saber qué tocar sin tener que buscar mil videos.'
          }, {
            name: 'Julieta M.',
            text: 'El ritual de noche fue lo que más me sirvió. Me dormí más rápido y me levanté con menos pesadez en el cuerpo.'
          }, {
            name: 'Rocío V.',
            text: 'Me dio una sensación de control sobre mi cuerpo que no tenía. Antes solo esperaba a que se me pasara.'
          }, {
            name: 'Martín G.',
            text: 'Trabajo muchas horas frente a la compu y siempre terminaba con el cuello duro y dolor de cabeza. Empecé con los puntos del oído y en pocos días sentí la diferencia. Ya no necesito tomar tantos analgésicos.'
          }, {
            name: 'Diego F.',
            text: 'Lo compré medio escéptico, pero la verdad es que el ritual de noche me cambió el descanso. Me duermo más rápido y me levanto sin esa pesadez de siempre. Súper recomendable.'
          }].map((test, i) => <div key={i} className="bg-white p-6 rounded-2xl shadow-sm border border-sand-100" data-id="element-228">
                <div className="flex text-gold-500 mb-4" data-id="element-229">
                  {[1, 2, 3, 4, 5].map(j => <Star key={j} className="w-4 h-4 fill-current" data-id="element-230" />)}
                </div>
                <p className="text-earth-700 italic mb-4" data-id="element-231">"{test.text}"</p>
                <p className="font-bold text-earth-900" data-id="element-232">— {test.name}</p>
              </div>)}
          </div>
        </div>
      </section>

      {/* NEW: Cómo funciona en 3 simples pasos */}
      <section className="py-20 px-4 bg-gradient-to-b from-white to-sand-50" data-id="element-233">
        <div className="max-w-5xl mx-auto" data-id="element-234">
          <div className="text-center mb-14" data-id="element-235">
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-sage-100 text-sage-900 text-xs sm:text-sm font-bold tracking-wide uppercase mb-4" data-id="element-236">
              <Leaf className="w-3.5 h-3.5" data-id="element-237" />
              Cómo funciona
            </div>
            <h2 className="text-3xl sm:text-4xl md:text-5xl font-serif font-bold text-earth-900 mb-4 leading-tight" data-id="element-238">
              En 3 simples pasos.{' '}
              <span className="text-terracotta-600 italic" data-id="element-239">
                Tan fácil como esto:
              </span>
            </h2>
            <p className="text-lg text-earth-700 max-w-2xl mx-auto" data-id="element-240">
              No tenés que estudiar. No tenés que memorizar nada. Solo seguir
              las guías paso a paso.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-6 sm:gap-8 relative" data-id="element-241">
            {/* Connector line */}
            <div className="hidden md:block absolute top-12 left-[16.66%] right-[16.66%] h-0.5 bg-gradient-to-r from-terracotta-200 via-gold-500 to-terracotta-200 -z-0" data-id="element-242"></div>

            {[{
            step: '1',
            title: 'Abrí el PDF',
            desc: 'Recibís acceso inmediato en tu mail. Lo abrís en el celular, la compu o la tablet.',
            icon: BookOpen
          }, {
            step: '2',
            title: 'Encontrá el punto exacto',
            desc: 'Buscá tu síntoma en las fichas rápidas. El mapa te muestra exactamente dónde tocar — con foto y descripción.',
            icon: Activity
          }, {
            step: '3',
            title: 'Presioná 2 a 5 minutos',
            desc: 'Con tus dedos, aplicás una presión suave en el punto indicado. Sentís el alivio empezar en la primera sesión.',
            icon: Heart
          }].map((s, i) => <div key={i} className="bg-white p-6 sm:p-8 rounded-3xl border border-sand-200 shadow-sm hover:shadow-lg transition-shadow relative z-10" data-id="element-243">
                <div className="w-24 h-24 mx-auto mb-5 rounded-full bg-gradient-to-br from-terracotta-500 to-terracotta-700 flex items-center justify-center text-white shadow-xl border-4 border-white" data-id="element-244">
                  <span className="text-4xl font-black font-serif" data-id="element-245">
                    {s.step}
                  </span>
                </div>
                <div className="flex items-center justify-center mb-3" data-id="element-246">
                  <s.icon className="w-6 h-6 text-terracotta-600" data-id="element-247" />
                </div>
                <h3 className="text-xl sm:text-2xl font-serif font-bold text-earth-900 text-center mb-3" data-id="element-248">
                  {s.title}
                </h3>
                <p className="text-earth-700 text-center leading-relaxed" data-id="element-249">
                  {s.desc}
                </p>
              </div>)}
          </div>

          {/* Result callout */}
          <div className="mt-12 bg-gradient-to-r from-gold-500/15 via-gold-500/25 to-gold-500/15 border-2 border-gold-500/40 rounded-3xl p-6 sm:p-8 text-center max-w-3xl mx-auto" data-id="element-250">
            <p className="text-sm font-bold uppercase tracking-wider text-earth-700 mb-2" data-id="element-251">
              ⏱ Resultado típico
            </p>
            <p className="text-xl sm:text-2xl font-serif font-bold text-earth-900" data-id="element-252">
              Empezás a sentir alivio en la{' '}
              <span className="text-terracotta-600" data-id="element-253">
                primera sesión de 5 minutos.
              </span>
            </p>
            <p className="text-earth-700 mt-2" data-id="element-254">
              Sin esperar semanas. Sin tratamientos eternos.
            </p>
          </div>

          <div className="mt-10 flex justify-center" data-id="element-255">
            <ButtonCTA text="QUIERO EMPEZAR HOY" showGuarantee data-id="element-256" />
          </div>
        </div>
      </section>

      {/* 8. Resultados típicos — Expectation timeline */}
      <section className="py-20 px-4 bg-earth-900 text-sand-50 relative overflow-hidden" data-id="element-257">
        <div className="absolute top-0 right-0 w-[600px] h-[600px] bg-terracotta-900/30 rounded-full blur-[100px] pointer-events-none" data-id="element-258"></div>

        <div className="max-w-5xl mx-auto relative z-10" data-id="element-259">
          <div className="text-center mb-14" data-id="element-260">
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-gold-500/20 border border-gold-500/40 text-gold-500 text-xs sm:text-sm font-bold tracking-wide uppercase mb-4" data-id="element-261">
              <Clock className="w-3.5 h-3.5" data-id="element-262" />
              Resultados típicos
            </div>
            <h2 className="text-3xl sm:text-4xl md:text-5xl font-serif font-bold text-white mb-4 leading-tight" data-id="element-263">
              Esto es lo que podés esperar
            </h2>
            <p className="text-lg text-earth-300 max-w-2xl mx-auto" data-id="element-264">
              Basado en lo que reportan las personas que ya usan el método.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-6" data-id="element-265">
            {[{
            time: '5 minutos',
            title: 'En tu primera sesión',
            outcomes: ['Sensación de calma en el cuerpo', 'Tensión liberada en cuello y mandíbula', 'Respiración más profunda']
          }, {
            time: '7 días',
            title: 'Después de una semana',
            outcomes: ['Menos episodios de dolor de cabeza', 'Cuerpo más liviano al despertar', 'Mejor descanso por la noche'],
            featured: true
          }, {
            time: '21 días',
            title: 'Después de 3 semanas',
            outcomes: ['Hábito instalado y natural', 'Tu cuerpo responde más rápido', 'Más control sobre tu bienestar diario']
          }].map((r, i) => <div key={i} className={`p-6 sm:p-7 rounded-3xl border ${r.featured ? 'bg-gradient-to-br from-terracotta-600/30 to-earth-800 border-gold-500 shadow-2xl scale-105' : 'bg-earth-800/60 border-earth-700'}`} data-id="element-266">
                <div className="text-gold-500 text-sm font-bold uppercase tracking-wider mb-2" data-id="element-267">
                  {r.time}
                </div>
                <h3 className="text-xl sm:text-2xl font-serif font-bold text-white mb-4" data-id="element-268">
                  {r.title}
                </h3>
                <ul className="space-y-2.5" data-id="element-269">
                  {r.outcomes.map((o, j) => <li key={j} className="flex items-start gap-2 text-sand-200" data-id="element-270">
                      <CheckCircle2 className="w-4 h-4 text-sage-400 flex-shrink-0 mt-0.5" data-id="element-271" />
                      <span className="text-sm sm:text-base" data-id="element-272">{o}</span>
                    </li>)}
                </ul>
              </div>)}
          </div>

          <div className="mt-10 text-center" data-id="element-273">
            <p className="text-earth-300 italic text-sm max-w-xl mx-auto" data-id="element-274">
              * Los resultados pueden variar según cada persona. Lo único que
              necesitás es darle a tu cuerpo la oportunidad de responder.
            </p>
          </div>

          <div className="mt-10 flex justify-center" data-id="element-275">
            <ButtonCTA text="QUIERO ESTOS RESULTADOS" showGuarantee data-id="element-276" />
          </div>
        </div>
      </section>

      {/* 9. FAQ Section */}
      <section className="py-20 px-4 bg-sand-50" data-id="element-277">
        <div className="max-w-4xl mx-auto" data-id="element-278">
          <div className="text-center mb-12" data-id="element-279">
            <h2 className="text-3xl sm:text-4xl font-serif font-bold text-earth-900 mb-4" data-id="element-280">
              Preguntas Frecuentes
            </h2>
          </div>

          <FAQAccordion items={[{
          question: '¿Y si no me funciona a mí?',
          answer: 'Tenés 7 días de garantía total. Si después de probar el método sentís que no es para vos, te devolvemos el 100% de tu dinero. Sin preguntas. Sin trabas. Es literalmente sin riesgo: o sentís alivio o recuperás tu plata.'
        }, {
          question: '¿No tengo tiempo. ¿Es complicado de usar?',
          answer: 'Cada práctica dura entre 5 y 15 minutos. No necesitás experiencia previa, no necesitás memorizar nada, no necesitás comprar materiales. Abrís el PDF, mirás la ficha de tu síntoma, presionás el punto indicado. Así de simple.'
        }, {
          question: '¿Por qué tan barato si vale $111.920?',
          answer: 'Porque es el precio de lanzamiento. Queremos que llegue a la mayor cantidad de personas posible. Por eso te llevás el bundle completo + los 6 bonos a un precio muy por debajo del valor real. Cuando termine la promo, vuelve al precio normal.'
        }, {
          question: '¿Cuándo recibo el producto?',
          answer: 'Recibís el acceso inmediatamente después de la compra en tu correo electrónico.'
        }, {
          question: '¿Tengo garantía de devolución?',
          answer: 'Sí. Tenés 7 días desde tu compra para probar el bundle completo. Si no te convence o sentís que no es para vos, escribinos y te devolvemos el 100% de tu dinero. Sin preguntas, sin trabas, sin letra chica.'
        }, {
          question: '¿Necesito experiencia previa?',
          answer: 'No. Está pensado para empezar desde cero, con explicaciones claras y visuales.'
        }, {
          question: '¿Necesito agujas?',
          answer: 'No. La auriculoterapia de esta guía se trabaja con presión, manos y opciones simples como semillas auriculares.'
        }, {
          question: '¿Necesito comprar materiales?',
          answer: 'No obligatoriamente. Podés empezar solo con tus manos. Los aceites y semillas son opcionales.'
        }, {
          question: '¿Sirve solo para ansiedad?',
          answer: 'No. También está pensado para dolor físico, contracturas, migrañas, tensión, insomnio, agotamiento y molestias corporales.'
        }, {
          question: '¿Cuánto tiempo lleva cada práctica?',
          answer: 'La mayoría de las prácticas duran entre 5 y 15 minutos, ideales para integrar en tu día a día.'
        }, {
          question: '¿Lo puedo hacer desde el celular?',
          answer: 'Sí. Son PDFs digitales, podés leerlos desde celular, compu o tablet.'
        }, {
          question: '¿Puedo imprimirlo?',
          answer: 'Sí, especialmente los mapas, fichas rápidas y diario están diseñados para que puedas imprimirlos si lo deseás.'
        }, {
          question: '¿Esto reemplaza un médico?',
          answer: 'No. Es una herramienta complementaria de bienestar natural. Si tenés dolor intenso, persistente o síntomas graves, consultá con un profesional.'
        }, {
          question: '¿Tengo acceso para siempre?',
          answer: 'Sí. Una vez que lo comprás, queda para vos para siempre.'
        }]} data-id="element-281" />
        </div>
      </section>

      {/* 10. Final CTA Section */}
      <section className="py-24 px-4 bg-earth-900 text-center relative overflow-hidden" data-id="element-282">
        <div className="absolute inset-0 bg-[url('https://images.unsplash.com/photo-1515377905703-c4788e51af15?auto=format&fit=crop&q=80')] bg-cover bg-center opacity-10 mix-blend-overlay" data-id="element-283"></div>
        <div className="absolute inset-0 bg-gradient-to-t from-earth-900 via-earth-900/80 to-transparent" data-id="element-284"></div>

        <div className="max-w-3xl mx-auto relative z-10 space-y-8" data-id="element-285">
          <h2 className="text-4xl sm:text-5xl font-serif font-bold text-sand-50" data-id="element-286">
            Tu cuerpo ya te viene avisando.
          </h2>

          <p className="text-xl text-earth-300" data-id="element-287">
            El dolor, la tensión, el cansancio y el insomnio no aparecen porque
            sí.
          </p>

          <p className="text-2xl font-serif text-sand-100 pb-4" data-id="element-288">
            Hoy podés seguir ignorándolo… o podés empezar a darle a tu cuerpo
            herramientas reales para aliviar, soltar y recuperarse.
          </p>

          <div className="bg-white/10 backdrop-blur-sm p-8 rounded-3xl border border-white/20 max-w-xl mx-auto" data-id="element-289">
            <p className="inline-flex items-center gap-2 bg-gold-500 text-earth-900 px-4 py-1.5 rounded-full font-bold mb-4 text-sm uppercase tracking-wider" data-id="element-290">
              <Flame className="w-4 h-4" data-id="element-291" />
              Oferta por tiempo limitado
            </p>
            <p className="text-lg text-sand-200 mb-2" data-id="element-292">
              Accedé al bundle completo por solo:
            </p>
            <div className="flex items-baseline justify-center gap-3 mb-2" data-id="element-293">
              <span className="text-xl text-sand-300/70 line-through" data-id="element-294">
                $111.920
              </span>
              <span className="text-6xl sm:text-7xl font-black text-gold-500 leading-none" data-id="element-295">
                $19.990
              </span>
              <span className="text-lg text-sand-300" data-id="element-296">ARS</span>
            </div>
            <p className="text-sand-300 mb-6 text-sm" data-id="element-297">
              Pago único · Acceso inmediato
            </p>
            <ButtonCTA text="SÍ, QUIERO EL BUNDLE AHORA" className="!w-full" data-id="element-298" />
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-earth-950 text-earth-500 py-8 text-center text-sm" data-id="element-299">
        <p data-id="element-300">
          © {new Date().getFullYear()} Bienestar Digital. Todos los derechos
          reservados.
        </p>
        <p className="mt-2 text-xs max-w-2xl mx-auto px-4" data-id="element-301">
          Este producto no sustituye el consejo médico profesional. Consulte a
          su médico antes de comenzar cualquier programa de bienestar.
        </p>
      </footer>

      {/* Floating urgency components */}
      <RecentPurchaseToast data-id="element-302" />
    </div>;
}